/**
 * ContratosSuspendidosModal (v1.5.0-rc12) — listado de contratos actualmente
 * suspendidos. El operador lo abre desde la alerta del dashboard para tomar
 * acción (cobrar, reactivar manualmente, llamar al cliente).
 *
 * NOTA: la suspensión es a nivel cliente — si el cliente tiene factura
 * vencida, TODOS sus contratos activos se suspenden. El cliente debe pagar
 * TODAS sus facturas pendientes para auto-reactivar todos sus servicios.
 * Esta nota se muestra abajo del modal como recordatorio.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Loader2,
  PauseCircle,
  Receipt,
  Wallet,
} from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { apiCall } from "@/lib/api";
import { formatMXN } from "@/lib/utils";

type ContratoSuspendido = {
  contrato_id: string;
  codigo: string;
  cliente_id: string;
  cliente_codigo: string;
  cliente_nombre: string;
  plan_id: string | null;
  plan_nombre: string;
  monto_adeudado: string;
  dias_suspendido: number | null;
  fecha_suspension: string | null;
};

type Response = {
  total: number;
  monto_adeudado_total: string;
  items: ContratoSuspendido[];
};

export function ContratosSuspendidosModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useQuery<Response>({
    queryKey: ["contratos-suspendidos-detalle"],
    queryFn: () =>
      apiCall<Response>("get", "/api/v1/dashboard/contratos-suspendidos"),
    enabled: open,
    refetchInterval: open ? 60_000 : false,
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()} size="xl">
      <div className="flex items-center justify-between border-b p-4 pr-12">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center">
            <PauseCircle size={18} className="text-orange-700 dark:text-orange-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold">Contratos suspendidos</h2>
            <p className="text-xs text-muted-foreground">
              {data
                ? `${data.total} contrato${data.total === 1 ? "" : "s"} suspendido${data.total === 1 ? "" : "s"} · adeudo total ${formatMXN(Number(data.monto_adeudado_total))}`
                : "cargando…"}
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            <Loader2 size={20} className="mx-auto mb-2 animate-spin" />
            cargando…
          </div>
        ) : isError ? (
          <div className="py-12 text-center text-sm text-destructive">
            Error al cargar — intenta refrescar
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-12 text-center">
            <CheckCircle2 size={32} className="mx-auto mb-3 text-emerald-500" />
            <p className="text-sm font-medium">Sin contratos suspendidos</p>
            <p className="text-xs text-muted-foreground mt-1">
              Todos tus contratos activos están al corriente.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Cliente</th>
                <th className="text-left px-3 py-2 font-medium">Plan</th>
                <th className="text-right px-3 py-2 font-medium">Adeudo</th>
                <th className="text-center px-3 py-2 font-medium">Suspendido</th>
                <th className="text-right px-4 py-2 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr
                  key={it.contrato_id}
                  className="border-t hover:bg-accent/30 transition-colors"
                >
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/clientes/${it.cliente_id}`}
                      className="hover:underline"
                    >
                      <div className="font-medium">{it.cliente_nombre}</div>
                      <div className="text-[10px] text-muted-foreground tabular-nums">
                        {it.cliente_codigo} · {it.codigo}
                      </div>
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">
                    {it.plan_nombre || "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-red-600 dark:text-red-400">
                    {formatMXN(Number(it.monto_adeudado))}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {it.dias_suspendido !== null ? (
                      <span
                        className={`tabular-nums text-xs font-semibold ${
                          it.dias_suspendido > 14
                            ? "text-red-600 dark:text-red-400"
                            : it.dias_suspendido > 7
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-foreground"
                        }`}
                      >
                        {it.dias_suspendido}d
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/facturas?cliente_id=${it.cliente_id}&estatus=vencida`}
                        title="Ver facturas vencidas"
                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                      >
                        <Receipt size={13} />
                      </Link>
                      <Link
                        to={`/pagos?cliente_id=${it.cliente_id}`}
                        title="Registrar pago"
                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                      >
                        <Wallet size={13} />
                      </Link>
                      <Link
                        to={`/contratos/${it.contrato_id}`}
                        title="Ver contrato"
                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                      >
                        <FileText size={13} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer + Disclaimer importante */}
      <div className="border-t bg-muted/30">
        <div className="px-4 py-2 border-b border-border/40">
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            💡 <strong className="text-foreground">Recordatorio</strong>: la
            suspensión es a nivel cliente. Si tiene factura vencida, TODOS sus
            contratos activos se suspenden. Para reactivar, el cliente debe pagar
            TODAS sus facturas pendientes; la reactivación es automática.
          </p>
        </div>
        <div className="p-3 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          {data && data.total > 0 && (
            <Link to="/contratos?estatus=suspendido" onClick={onClose}>
              <Button size="sm" className="gap-1">
                Ver lista completa
                <ArrowRight size={12} />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </Dialog>
  );
}
